import type { ICellRendererParams } from 'ag-grid-community'
import {
  entries,
  filter,
  find,
  forEach,
  isEmpty,
  isNullish,
  isNumber,
  isPlainObject,
  isString,
  pipe,
  values
} from 'remeda'
import { iconHTML } from '../utils/dom'
import {
  extractEditorDisplayValue,
  isPendingRawDisplayCell
} from '../utils/editor-value'
import { createTemplateRenderer } from '../utils/fast-renderer'

// Redirect data type from backend
type RedirectData = {
  readonly type?: string | null
  readonly url?: string | null
  readonly value?: string | number | null
  readonly label?: string | number | null
  readonly attrs?: Record<string, string> | null
}

type RedirectRenderState =
  | { readonly status: 'empty' }
  | { readonly status: 'pending'; readonly value: string | number }
  | { readonly status: 'ready'; readonly data: RedirectData }

const isRedirectText = (value: unknown): value is string | number =>
  isString(value) || (isNumber(value) && Number.isFinite(value))

const isOptional = <Value>(
  value: unknown,
  predicate: (candidate: unknown) => candidate is Value
): value is Value | null | undefined => isNullish(value) || predicate(value)

const isStringRecord = (value: unknown): value is Record<string, string> =>
  isPlainObject(value) &&
  pipe(
    value,
    values(),
    filter((item) => !isString(item)),
    isEmpty
  )

const isRedirectData = (value: unknown): value is RedirectData =>
  isPlainObject(value) &&
  isOptional(value.type, isString) &&
  isOptional(value.url, isString) &&
  isOptional(value.value, isRedirectText) &&
  isOptional(value.label, isRedirectText) &&
  isOptional(value.attrs, isStringRecord)

const resolveRedirectState = (
  value: unknown,
  params: ICellRendererParams
): RedirectRenderState => {
  const columnId = params.column?.getColId()
  const cellValue = columnId ? params.data?.[columnId] : undefined

  if (isPendingRawDisplayCell(cellValue)) {
    const pendingValue = extractEditorDisplayValue(cellValue)

    return isRedirectText(pendingValue)
      ? { status: 'pending', value: pendingValue }
      : { status: 'empty' }
  }

  const redirectData = pipe([cellValue, value], find(isRedirectData))

  if (redirectData) {
    return { status: 'ready', data: redirectData }
  }

  return isRedirectText(value)
    ? { status: 'ready', data: { value } }
    : { status: 'empty' }
}

const displayText = (value: string | number | null | undefined): string =>
  isNullish(value) ? '' : String(value)

/**
 * Static redirect skeleton — parsed ONCE, cloned per cell. Mirrors the markup
 * of the former `redirectHTML`, but only the arrow carries the Phoenix
 * `data-phx-link*` attrs so clicking the text keeps the grid cell selectable.
 */
const REDIRECT_TEMPLATE =
  '<div class="grid-redirect">' +
  '<span class="grid-redirect-text"></span>' +
  '<a class="grid-redirect-link" data-phx-link-state="push">' +
  iconHTML('arrow-circle-right') +
  '</a>' +
  '</div>'

const STATIC_REDIRECT_ATTRS = new Set(['class', 'data-phx-link-state'])

// Reset a reused cell node so it flips cleanly between link / action / empty modes.
const clearDynamicAttrs = (link: HTMLAnchorElement): void => {
  pipe(
    link.getAttributeNames(),
    filter((name) => !STATIC_REDIRECT_ATTRS.has(name)),
    forEach((name) => {
      link.removeAttribute(name)
    })
  )
}

const updateRedirect = (
  root: HTMLElement,
  state: RedirectRenderState
): void => {
  const link = root.querySelector<HTMLAnchorElement>('.grid-redirect-link')!
  const textEl = root.querySelector<HTMLElement>('.grid-redirect-text')!

  clearDynamicAttrs(link)
  root.style.display = ''

  if (state.status === 'empty') {
    root.style.display = 'none'
    textEl.textContent = ''
    return
  }

  if (state.status === 'pending') {
    link.hidden = true
    textEl.textContent = displayText(state.value)
    return
  }

  const { data } = state
  const text = displayText(data.label ?? data.value)

  // Action mode: a phx-click anchor with no href — Phoenix delegates the click.
  if (data.attrs) {
    link.setAttribute('role', 'button')

    forEach(entries(data.attrs), ([key, value]) => {
      link.setAttribute(key, value)
    })

    link.setAttribute('title', text)
    link.setAttribute('aria-label', text)
    textEl.textContent = text
    return
  }

  if (!text) {
    root.style.display = 'none'
    textEl.textContent = ''
    return
  }

  if (isNullish(data.url)) {
    link.hidden = true
    textEl.textContent = text
    return
  }

  link.setAttribute('href', data.url)
  link.setAttribute('title', text)
  link.setAttribute('aria-label', text)
  link.setAttribute('data-phx-link', data.type ?? '')
  textEl.textContent = text
}

/** Redirect cell renderer — clones a pre-parsed template, no innerHTML on refresh */
export const RedirectComponent = createTemplateRenderer<unknown>(
  REDIRECT_TEMPLATE,
  (root, data, params: ICellRendererParams) =>
    updateRedirect(root, resolveRedirectState(data, params))
)

export type { RedirectData }
